/**
 *
 * InputField
 *
 * ------------
 *
 * The prop `labelClassName` is needed to override the border color behind
 * the label text.
 *
 * ex:
 *
 * passing 'channel-page__field-label' as `labelClassName`
 * and creating the CSS (SCSS) rule:
 *
 * ```css
 * .input-field__label.channel-page__field-label:before {
 *   border-top: 1px solid #000000;
 * }
 * ```
 *
 * allows the line behind the label to be #000000
 *
 * NOTE: this color should match the color of the background to
 * correctly reflect the mockups
 *
 * ------------
 *
 */

import React from 'react';
import {
  EditBtn,
  RightIcon,
  InPlaceBtn,
  InPlaceButtons,
  Input,
  Label,
  PasswordButton,
  SelectIcon,
  TextArea,
  Wrapper,
  StyledRPS
} from "./styledComponents";

import Date from './Date';
import File from './File';
import Icon from 'components/Icon';
import InputValidator from 'components/InputValidator';
import Select from './Select';
import { colors } from 'globalStyles';
import Tooltip from 'components/Tooltip';
import { connect } from "react-redux";
import { setRpsPasswordStatus } from "./actions";

class InputField extends React.PureComponent {
  state = {
    showPassword: true,
    inPlaceOpen: false,
    hasPasswordError: true,
    password: ""
  };

  togglePasswordType = () =>
    this.setState({ showPassword: !this.state.showPassword });

  toggleInPlace = () => {
    this.setState({ inPlaceOpen: !this.state.inPlaceOpen}, () => {
      if(this.state.inPlaceOpen && this.input_field && this.input_field.focus) {
        this.input_field.focus();
      }
    })
  }

  componentDidMount = () => {
    this.initialValue = this.props.input.value
  }

  componentDidUpdate(prevProps) {
    if (this.props.input.name === "slug" && 
    prevProps.input.value !== this.props.input.value) {
      if (prevProps.meta.initial !== this.props.input.value) {
        this.props.validateSlug(this.props.input.value);
      }
      /* When a slug input value === initial slug, this 
      generates a random string to check that will 
      (99.9999999992% of the time) pass validation */
      else(this.props.validateSlug(Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)));
    }
  }
  
  cancel = () => {
    this.toggleInPlace()
  }

  render() {
    const {
      inPlace,
      input: { onFocus: inputOnFocus, onChange: inputOnChange, ...input },
      label,
      editBtnText,
      updateBtnText,
      customActionBtn,
      type,
      placeholder,
      autoComplete,
      meta: { touched, error, valid },
      inputClassName,
      labelClassName,
      iconClassName,
      validIconName,
      errorIconName,
      emptyIconName,
      onFocus,
      onChange,
      inPlaceOnSubmit,
      showPasswordButton,
      helpEvent,
      helpIcon,
      disabled,
      multiline,
      leftIcon,
      validateIcon,
      tooltip,
      isSignup = false,
      setRpsPasswordStatus,
    } = this.props;

    const handlePasswordStrength = (passwordStrength) => {
      const { isValid, password } = passwordStrength;
      if (isValid) {
        this.setState({ ...this.state, hasPasswordError: false, password });
        setRpsPasswordStatus(false);
      } else {
        this.setState({ ...this.state, hasPasswordError: true, password: "••••••••••••" });
        setRpsPasswordStatus(true);
      }
  
      return isValid ? inputOnChange(password) : inputOnChange(passwordStrength);
    };
    const { showPassword, inPlaceOpen } = this.state;
    const hasError = type === 'password' && label !== 'Gallery Password' ? touched && this.state.hasPasswordError : touched && error;

    const isValid = touched && valid;

    const inPlaceAttr = Object.assign({},
      inPlace && !inPlaceOpen ? {disabled: true} : {})
      const InputComponent =  multiline ? TextArea : Input;

    return (
      <Wrapper className={inPlace ? "inPlace" : undefined}>
        {type === "password" ? 
          isSignup ? (
            <StyledRPS
              {...inPlaceAttr}
              minLength={8}
              minScore={2}
              innerRef={(_input) => {
                this.input_field = _input;
              }}
              className="CustomInput"
              tooShortWord="too short"
              onFocus={inputOnFocus}
              changeCallback={(state) => handlePasswordStrength(state)}
              inputProps={{
                ...input,
                name: "password",
                type: showPassword ? "password" : "text",
                disabled: disabled,
                value: this.state.password,
                placeholder: placeholder,
              }}
              hasError={this.state.hasPasswordError}
            />
          ) : 
            <Input
              {...inPlaceAttr}
              className={inputClassName + ((inPlace && !inPlaceOpen) ? ' inPlace ' : undefined)}
              ref={(_input) => { this.input_field = _input; }}
              hasError={hasError}
              {...input}
              onFocus={onFocus ? onFocus : inputOnFocus}
              onChange={onChange? onChange: inputOnChange}
              type={showPassword ? 'password' : 'text'}
              placeholder={placeholder}
              autoComplete={autoComplete || 'on'}
              disabled={disabled}
            />
          :
            <InputComponent
              {...inPlaceAttr}
              className={inputClassName + (leftIcon ? ' has-icon ' : '') + ((inPlace && !inPlaceOpen) ? ' inPlace ' : undefined)}
              innerRef={(_input) => { this.input_field = _input; }}
              hasError={hasError}
              {...input}
              onFocus={onFocus ? onFocus : inputOnFocus}
              onChange={onChange? onChange: inputOnChange}
              type={type}
              placeholder={placeholder}
              autoComplete={autoComplete || 'on'}
              disabled={disabled}
              rows={multiline}
            />
        }
        {label && <Label className={`${labelClassName} ${multiline ? 'multi-select' : ''}`}>
          {label}
        </Label>}
        {leftIcon && <SelectIcon className='left-icon'>{leftIcon}</SelectIcon>}
        {
          (inPlace && !inPlaceOpen)
          ?
            <EditBtn onClick={() => {
              if(customActionBtn) {
                customActionBtn()
              }  else {
                this.toggleInPlace()
              }
            }}>{editBtnText ? editBtnText : 'Edit'.toUpperCase()}{editBtnText ? undefined : <span> {label}</span>}</EditBtn>
          :
            undefined
        }
        {
          (inPlace && inPlaceOpen)
          ?
            <InPlaceButtons>
              <InPlaceBtn
                className="cancel"
                onClick={() => {
                  inputOnChange(this.initialValue)
                  this.cancel()
                }}
              >cancel</InPlaceBtn>
              <InPlaceBtn
                className="submit"
                type="submit"
                onClick={() => {
                  inPlaceOnSubmit({[input.name]: input.value})
                  this.toggleInPlace()
                }}
              >
                <span>{updateBtnText ? updateBtnText :'update ' + label}</span>
                <span>confirm</span>
              </InPlaceBtn>
            </InPlaceButtons>
          :
            undefined
        }

        <InputValidator
          hasError={hasError}
          isValid={isValid}
          iconClassName={iconClassName}
          validIconName={validIconName}
          errorIconName={errorIconName}
          emptyIconName={emptyIconName}
        />
        {showPasswordButton ?
          type === "password" && !inPlace &&
            <PasswordButton type='button' onClick={this.togglePasswordType}>
              {showPassword ? 'show' : 'hide'}
            </PasswordButton>

          : ''
        }
        {helpIcon && helpEvent ? <RightIcon type="button" onClick={helpEvent}><Icon name="help" size="10" /></RightIcon> : null}
        {validateIcon ? <RightIcon><Icon name="check" size="10" fill={colors.GREEN} /></RightIcon> : null}
        {tooltip && <Tooltip className='tooltip' width='200px' text={tooltip}>
          <Icon size='12' name='help' />
        </Tooltip>}
      </Wrapper>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  setRpsPasswordStatus: (status) => dispatch(setRpsPasswordStatus(status)),
})

export default connect(null, mapDispatchToProps)(InputField)
export {
  Select,
  File,
  Date,
};



// WEBPACK FOOTER //
// ./src/components/InputField/index.js